import { useEffect, useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useQuery } from "@apollo/client/react";
import Skeleton from "react-loading-skeleton";
import { castPrice } from "@ct-react/core";
import { useLocaleFormatter, useLocaleNavigate } from "@ct-react/locale";
import { calculateCartPaymentAmount, CartPaymentMode } from "@ct-react/calendar";
import { FULL_CART_GQL_DATA, useCheckoutContext } from "./outlet";
import CartPrice from "../../components/cart/price";
import "./common.scss";
import "./payment.scss";

const transDefs = defineMessages({
  modeTitle: { id: "cart.payment.mode-title", defaultMessage: "Modèle de paiement" },
  fullTitle: { id: "cart.payment.mode-full-title", defaultMessage: "Payer le montant total" },
  fullDetail: { id: "cart.payment.mode-full-detail", defaultMessage: "Payez le total maintenant et vous aurez terminé." },
  partialTitle: { id: "cart.payment.mode-partial-title", defaultMessage: "Payer une partie maintenant et l'autre plus tard" },
  partialDetail: { id: "cart.payment.mode-partial-detail", defaultMessage: "Payez {down} maintenant et le solde ({diff}) vous sera prélevé ultérieurement." },
  partialFee: { id: "cart.payment.mode-partial-fee", defaultMessage: "Aucun frais supplémentaires." },
  partialDisabled: { id: "cart.payment.mode-partial-disabled", defaultMessage: "Cette option n'est pas disponible pour cette réservation." }
});

const PaymentPage = () => {

  const intl = useIntl();
  const { print } = useLocaleFormatter();
  const navigate = useLocaleNavigate();

  const [ paymentMode, setPaymentMode ] = useState<CartPaymentMode>("full");
  const [ { sessionId, userId, customer }, dispatch ] = useCheckoutContext();
  const { data, loading } = useQuery<any>(FULL_CART_GQL_DATA, { variables: { sessionId, userId } });

  useEffect(() => {
    if (loading) return;
    if ((data?.cart?.items?.length || 0) === 0)
      navigate("/checkout/", { replace: true });
    else if (!customer)
      navigate("/checkout/information", { replace: true });
    else
      dispatch({ type: "change.index", payload: { index: 3, valid: true } });
  }, [ loading, data ]);

  useEffect(() => dispatch({ type: "change.payment.mode", payload: paymentMode }), [ paymentMode ]);

  const modeDetails = useMemo(() => {
    if (!data?.cart?.items) return undefined;
    const full = calculateCartPaymentAmount(data.cart.items, "full");
    const partial = calculateCartPaymentAmount(data.cart.items, "partial");
    const diff = castPrice({ amount: full.total.amount - partial.total.amount, currency: full.total.currency });
    const downable = diff.amount > 0
    return { full, partial, diff, downable };
  }, [ data ]);


  // rendering

  if (loading || !data || !data.cart || !modeDetails)
    return (<Skeleton className="cart-skeleton" />);

  return (
    <div className="two-col">

      <div>
        <h2>{intl.formatMessage(transDefs.modeTitle)}</h2>
        <form className="payment-choice">
          <label className="payment-radio">
            <input type="radio"
                   name="mode"
                   id="full"
                   value="full"
                   checked={paymentMode === "full"}
                   onChange={() => setPaymentMode("full")} />
            <span className="radio-content">
              <span className="label">{intl.formatMessage(transDefs.fullTitle)}</span>
              <label htmlFor="full">{print.price(modeDetails.full.total)}</label>
              <span className="checkmark" />
              <span className="detail">{intl.formatMessage(transDefs.fullDetail)}</span>
            </span>
          </label>
          <label className="payment-radio">
            <input type="radio"
                   name="mode"
                   id="partial"
                   value="partial"
                   disabled={!modeDetails.downable}
                   checked={paymentMode === "partial"}
                   onChange={() => setPaymentMode("partial")} />
            <span className="radio-content">
              <span className="label">{intl.formatMessage(transDefs.partialTitle)}</span>
              {modeDetails.downable &&
                <label htmlFor="full">{print.price(modeDetails.partial.total)}</label>
              }
              <span className="checkmark" />
              {modeDetails.downable &&
                <span className="detail">
                  <span className="block">
                    {intl.formatMessage(transDefs.partialDetail, {
                      down: print.price(modeDetails.partial.total),
                      diff: print.price(modeDetails.diff)
                    })}
                  </span>
                  <span className="block">{intl.formatMessage(transDefs.partialFee)}</span>
                </span>
              }
              {!modeDetails.downable &&
                <span className="detail">
                  <span className="block">{intl.formatMessage(transDefs.partialDisabled)}</span>
                </span>
              }
            </span>
          </label>
        </form>
      </div>

      <div className="spacing">
        <CartPrice data={{
          price: data.cart.fullPrice,
          toPay: { mode: paymentMode, detail: modeDetails[paymentMode] }
        }} />
      </div>

    </div>);

}

export default PaymentPage;
